/**
 * Connections API Service
 * 
 * Provides a clean interface for the connections model:
 * Household → Connections → Accounts → Transactions
 * 
 * IMPORTANT: Uses empty string for API_URL to ensure same-origin requests
 * and proper cookie handling. The ingress handles routing to backend.
 */

// Always use empty string for same-origin requests - cookies require this
const API_URL = '';

/**
 * Fetch all connections for the current user's household
 */
export async function fetchConnections() {
  const response = await fetch(`${API_URL}/api/connections`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch connections');
  }
  return response.json();
}

/**
 * Get or create the manual connection for a household
 */
export async function ensureManualConnection() {
  const response = await fetch(`${API_URL}/api/connections/manual`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    throw new Error('Failed to ensure manual connection');
  }
  return response.json();
}

/**
 * Get accounts for a specific connection
 */
export async function fetchConnectionAccounts(connectionId) {
  const response = await fetch(`${API_URL}/api/connections/${connectionId}/accounts`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch connection accounts');
  }
  return response.json();
}

/**
 * Create an account under a connection
 */
export async function createConnectionAccount(connectionId, accountData) {
  const response = await fetch(`${API_URL}/api/connections/${connectionId}/accounts`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(accountData)
  });
  if (!response.ok) {
    throw new Error('Failed to create account');
  }
  return response.json();
}

/**
 * Disconnect a connection (revoke access, optionally delete accounts)
 */
export async function disconnectConnection(connectionId, deleteAccounts = false) {
  const response = await fetch(
    `${API_URL}/api/connections/${connectionId}?delete_accounts=${deleteAccounts}`,
    {
      method: 'DELETE',
      credentials: 'include'
    }
  );
  if (!response.ok) {
    throw new Error('Failed to disconnect');
  }
  return response.json();
}

/**
 * Migrate legacy accounts to the connections model
 */
export async function migrateLegacyAccounts() {
  const response = await fetch(`${API_URL}/api/connections/migrate-legacy`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to migrate accounts');
  }
  return response.json();
}

/**
 * Fetch all accounts grouped by connection
 */
export async function fetchAccountsByConnection() {
  const { connections } = await fetchConnections();
  
  const result = [];
  for (const conn of connections) {
    const { accounts } = await fetchConnectionAccounts(conn.connection_id);
    result.push({
      ...conn,
      accounts
    });
  }
  
  return result;
}

/**
 * Get connection provider display info
 */
export function getProviderInfo(provider) {
  const providers = {
    manual: {
      name: 'Manual',
      description: 'Manually entered accounts',
      icon: 'edit',
      color: 'slate'
    },
    akahu: {
      name: 'Akahu',
      description: 'NZ Open Banking',
      icon: 'building',
      color: 'emerald'
    },
    plaid: {
      name: 'Plaid',
      description: 'International banking',
      icon: 'globe',
      color: 'blue'
    },
    csv: {
      name: 'CSV Import',
      description: 'Imported from file',
      icon: 'file',
      color: 'amber'
    }
  };
  
  return providers[provider] || providers.manual;
}

/**
 * Get connection status display info
 */
export function getStatusInfo(status) {
  const statuses = {
    active: {
      label: 'Connected',
      color: 'emerald',
      canSync: true
    },
    disconnected: {
      label: 'Disconnected',
      color: 'slate',
      canSync: false
    },
    error: {
      label: 'Error',
      color: 'red',
      canSync: false
    },
    pending: {
      label: 'Pending',
      color: 'amber',
      canSync: false
    },
    expired: {
      label: 'Expired',
      color: 'orange',
      canSync: false
    }
  };
  
  return statuses[status] || statuses.disconnected;
}

export default {
  fetchConnections,
  ensureManualConnection,
  fetchConnectionAccounts,
  createConnectionAccount,
  disconnectConnection,
  migrateLegacyAccounts,
  fetchAccountsByConnection,
  getProviderInfo,
  getStatusInfo
};
